import React, { useEffect, useState } from 'react';
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import {
  IonApp, IonTabs, IonTabBar, IonTabButton, IonRouterOutlet,
  IonIcon, IonLabel, IonSpinner, setupIonicReact,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { statsChart, layers, albums, time, swapHorizontal } from 'ionicons/icons';
import { useAuthStore } from './store/authStore';
import { API_BASE, getStoredToken, clearStoredToken } from './config/api';

import Dashboard from './pages/Dashboard';
import CardList from './pages/CardList';
import CardForm from './pages/CardForm';
import CardDetail from './pages/CardDetail';
import BinderList from './pages/BinderList';
import BinderForm from './pages/BinderForm';
import BinderDetail from './pages/BinderDetail';
import SettingsPage from './pages/Settings';
import ActivityLog from './pages/ActivityLog';
import EventForm from './pages/EventForm';
import CashTransactionForm from './pages/CashTransactionForm';
import Login from './pages/Login';
import Register from './pages/Register';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

setupIonicReact();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});


const AuthenticatedApp: React.FC = () => {
  return (
    <IonTabs>
      <IonRouterOutlet>
        <Route exact path="/dashboard" component={Dashboard} />
        <Route exact path="/trades/new" component={EventForm} />
        <Route exact path="/history/new" component={EventForm} />
        <Route exact path="/history/cash" component={CashTransactionForm} />
        <Route exact path="/history" component={ActivityLog} />
        <Route exact path="/cards" component={CardList} />
        <Route exact path="/cards/:id" component={CardDetail} />
        <Route exact path="/cards/:id/edit" component={CardForm} />
        <Route exact path="/binders" component={BinderList} />
        <Route exact path="/binders/:id" component={BinderDetail} />
        <Route exact path="/binders/:id/edit" component={BinderForm} />
        <Route exact path="/settings" component={SettingsPage} />
        <Redirect exact from="/" to="/dashboard" />
      </IonRouterOutlet>
      <IonTabBar slot="bottom">
        <IonTabButton tab="dashboard" href="/dashboard">
          <IonIcon icon={statsChart} />
          <IonLabel>Dashboard</IonLabel>
        </IonTabButton>
        <IonTabButton tab="cards" href="/cards">
          <IonIcon icon={layers} />
          <IonLabel>Cards</IonLabel>
        </IonTabButton>
        <IonTabButton tab="binders" href="/binders">
          <IonIcon icon={albums} />
          <IonLabel>Binders</IonLabel>
        </IonTabButton>
        <IonTabButton tab="trades" href="/trades/new">
          <IonIcon icon={swapHorizontal} />
          <IonLabel>Trades</IonLabel>
        </IonTabButton>
        <IonTabButton tab="history" href="/history">
          <IonIcon icon={time} />
          <IonLabel>History</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
};

const UnauthenticatedApp: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (location.pathname !== '/login' && location.pathname !== '/register') {
      history.replace('/login');
    }
  }, [location.pathname, history]);

  if (location.pathname === '/register') {
    return <Register />;
  }
  return <Login />;
};

const App: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      // Validate token using fetch to avoid the axios 401 interceptor
      fetch(`${API_BASE}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          if (!res.ok) throw new Error('Invalid token');
          return res.json();
        })
        .then((user) => {
          useAuthStore.getState().setAuth(user, token);
          setReady(true);
        })
        .catch(() => {
          clearStoredToken();
          setReady(true);
        });
    } else {
      setReady(true);
    }
  }, []);

  if (!ready) {
    return (
      <IonApp>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <IonSpinner />
        </div>
      </IonApp>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <IonApp>
        <IonReactRouter>
          {isAuthenticated ? (
            <AuthenticatedApp />
          ) : (
            <UnauthenticatedApp />
          )}
        </IonReactRouter>
      </IonApp>
    </QueryClientProvider>
  );
};

export default App;
